<template>
  <div class="cer-stat" :style="trackWrapStyle">
    <div class="cer-stat__block">
      <div class="cer-stat__bar">
        <div class="cer-stat__bar-track" aria-hidden="true">
          <div
            class="cer-stat__bar-fill"
            :class="{
              'cer-stat__bar-fill--full': fillPercent >= 100,
              'cer-stat__bar-fill--visible-min': fillPercent > 0 && fillPercent < 2,
            }"
            :style="{ width: fillWidth }"
          />
        </div>
        <span
          class="cer-stat__val"
          :class="{ 'cer-stat__val--on-fill': valueOnFill }"
          :style="valueLabelStyle"
          :title="String(displayVal)"
        >
          {{ prunNum(displayVal) }}
        </span>
      </div>

      <div class="cer-stat__range-labels">
        <span
          class="cer-stat__min"
          :title="minHoverTitle"
        >{{ prunNum(displayMin) }}</span>
        <span
          class="cer-stat__max"
          :title="maxHoverTitle"
        >{{ prunNum(displayMax) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { smartPruneNumber } from "@/utils/number";

export default {
  name: "CastingExploreResultStatistics",
  props: {
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    val: {
      type: Number,
      required: true,
    },
    propertyName: {
      type: String,
      default: "",
    },
    width: {
      type: Number,
      default: undefined,
    },
  },
  computed: {
    rawMin() {
      return Number(this.min);
    },
    rawMax() {
      return Number(this.max);
    },
    rawVal() {
      return Number(this.val);
    },
    displayMin() {
      const n = Number(this.min);
      return Number.isFinite(n) ? smartPruneNumber(n) : n;
    },
    displayMax() {
      const n = Number(this.max);
      return Number.isFinite(n) ? smartPruneNumber(n) : n;
    },
    displayVal() {
      const n = Number(this.val);
      return Number.isFinite(n) ? smartPruneNumber(n) : n;
    },
    propertyLabel() {
      return String(this.propertyName || "this property").trim() || "this property";
    },
    minHoverTitle() {
      return `Min of ${this.propertyLabel} in the database`;
    },
    maxHoverTitle() {
      return `Max of ${this.propertyLabel} in the selected database`;
    },
    fillPercent() {
      const min = this.rawMin;
      const max = this.rawMax;
      const val = this.rawVal;

      if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(val)) {
        return 0;
      }

      if (max <= min) {
        return val >= max ? 100 : 0;
      }

      const range = max - min;
      const epsilon = Math.max(range * 1e-6, 1e-9);

      if (val <= min + epsilon) return 0;
      if (val >= max - epsilon) return 100;

      let percent = ((val - min) / range) * 100;

      // When display rounds predicted value to max, fill should reach the end.
      if (this.displayVal >= this.displayMax) {
        return 100;
      }

      return Math.min(100, Math.max(0, percent));
    },
    valueOnFill() {
      return this.fillPercent >= 15;
    },
    fillWidth() {
      return this.fillPercent >= 100 ? "100%" : `${this.fillPercent}%`;
    },
    valueLabelStyle() {
      const percent = this.fillPercent;
      const inset = "var(--cer-stat-val-inset)";

      if (percent <= 0) {
        return { left: inset, right: "auto", transform: "translate(0, -50%)" };
      }

      if (percent >= 85) {
        return {
          left: `calc(${percent}% - ${inset})`,
          right: "auto",
          transform: "translate(-100%, -50%)",
        };
      }

      if (percent <= 15) {
        return {
          left: `calc(${percent}% + ${inset})`,
          right: "auto",
          transform: "translate(0, -50%)",
        };
      }

      return {
        left: `${percent}%`,
        right: "auto",
        transform: "translate(-50%, -50%)",
      };
    },
    trackWrapStyle() {
      if (this.width == null || this.width === 0) {
        return { width: "100%", maxWidth: "100%", boxSizing: "border-box" };
      }
      return { width: "100%", maxWidth: `${this.width}px`, boxSizing: "border-box" };
    },
  },
  methods: {
    prunNum(num) {
      return smartPruneNumber(num);
    },
  },
};
</script>

<style scoped>
.cer-stat {
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
}

.cer-stat__block {
  --cer-stat-bar-h: 1.375rem;
  --cer-stat-val-inset: 0.5rem;
  width: 100%;
  min-width: 0;
}

.cer-stat__bar {
  position: relative;
  width: 100%;
  min-width: 0;
  height: var(--cer-stat-bar-h);
  border-radius: var(--radius-sm);
  box-sizing: border-box;
  overflow: hidden;
}

.cer-stat__bar-track {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: var(--color-gray-200);
  overflow: hidden;
}

html.dark .cer-stat__bar-track {
  background-color: color-mix(in srgb, var(--color-gray-700) 55%, transparent);
}

.cer-stat__bar-fill {
  height: 100%;
  min-width: 0;
  border-radius: 0;
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--v-primary-base) 55%, white),
    var(--v-primary-base)
  );
  transition: width 0.15s ease;
}

html.dark .cer-stat__bar-fill {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--v-primary-base) 45%, var(--color-gray-800)),
    var(--v-primary-base)
  );
}

.cer-stat__bar-fill--visible-min {
  min-width: 3px;
}

.cer-stat__bar-fill--full {
  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
}

.cer-stat__val {
  position: absolute;
  top: 50%;
  z-index: 2;
  max-width: calc(100% - 1rem);
  padding: 0 0.25rem;
  font-size: 0.6875rem;
  line-height: 1;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--color-gray-800);
  text-shadow: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.cer-stat__val--on-fill {
  color: var(--color-white);
  text-shadow: 0 1px 2px rgb(0 0 0 / 0.35);
  padding-right: 0.125rem;
}

html.dark .cer-stat__val {
  color: var(--color-gray-200);
}

html.dark .cer-stat__val--on-fill {
  color: var(--color-white);
}

.cer-stat__range-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  width: 100%;
  min-width: 0;
}

.cer-stat__min,
.cer-stat__max {
  flex: 0 1 auto;
  max-width: 48%;
  min-width: 0;
  font-size: 0.6875rem;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.cer-stat__min {
  text-align: left;
  color: #dc2626;
}

.cer-stat__max {
  text-align: right;
  color: #16a34a;
}

html.dark .cer-stat__min {
  color: #f87171;
}

html.dark .cer-stat__max {
  color: #4ade80;
}
</style>
